import { Divider, FormControlLabel, Checkbox, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { ReactComponent as Bed } from 'assets/svg/bedrooms.svg';
import { ReactComponent as Budget } from 'assets/svg/budget.svg';
import { ReactComponent as ConstructedArea } from 'assets/svg/constructed-area.svg';
import { ReactComponent as FeatureBuildAreaSvg } from 'assets/svg/feature-buildarea.svg';
import { ReactComponent as Commission } from 'assets/svg/percentage-square.svg';
import { ReactComponent as Urgency } from 'assets/svg/urgency.svg';
import { ReactComponent as DefaultProperty } from 'assets/svg/default-property.svg';
import { CityFilter, CountryFilter } from 'components/Filter';
import StateFilter from 'components/Filter/StateFilter';
import NeighborhoodFilter from 'components/Filter/NeighborhoodFilter';
import { ModalLayoutProps } from 'components/Modals';
import ModalLayout from 'components/Modals/ModalLayout';
import { useState, useEffect } from 'react';
import usePropertyRequestsStore from 'store/propertyRequestsStore';
import { PropertyLeadRequestKeys } from 'types/PropertyLead';
import { PROPERTY_TYPES, MALLORCA_NOT_DEFINED_LOCATIONS } from 'util/Constants';
import { clearFilter, formatFilterStrings } from 'util/formatFilterStrings';
import { urgencyOptions } from 'util/urgencySelector';
import AppliedFilters from './AppliedFilters';
import Dropdown from './DropDown';
import RangeFilter from './RangeFilter';
import { useGetBuyerProfiles } from 'hooks/usePropertyRequests';

export default function Filters({ visible, onClose }: ModalLayoutProps) {
    const [errors, setErrors] = useState<Record<PropertyLeadRequestKeys, string>>({} as Record<PropertyLeadRequestKeys, string>);
    const { propertyRequestQueryFilter, setPropertyRequestQueryFilter, setLocalFilters, localFilters, region, setRegion } = usePropertyRequestsStore();
    const [usePredefinedLocations, setUsePredefinedLocations] = useState<boolean>(false);
    const [selectedPredefinedLocation, setSelectedPredefinedLocation] = useState<string>('');
    const { data: availableProfiles = [] } = useGetBuyerProfiles();

    // Initialize localFilters with current propertyRequestQueryFilter values when modal opens
    useEffect(() => {
        if (visible) {
            setLocalFilters({
                ...propertyRequestQueryFilter,
                minBudget: propertyRequestQueryFilter.minBudget || 0,
                maxBudget: propertyRequestQueryFilter.maxBudget || 0,
            });
        }
    }, [visible, propertyRequestQueryFilter, setLocalFilters]);

    useEffect(() => {
        // Set initial state based on existing filter
        if (localFilters.definedArea) {
            setUsePredefinedLocations(true);
            setSelectedPredefinedLocation(localFilters.definedArea);
        } else {
            setUsePredefinedLocations(false);
            setSelectedPredefinedLocation('');
        }
    }, [localFilters.definedArea]);

    const updateLocalFilters = (updates: Partial<typeof propertyRequestQueryFilter>) => setLocalFilters(updates);

    const handlePredefinedLocationsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        setUsePredefinedLocations(checked);

        if (checked) {
            // When enabling predefined locations, clear propertyState and propertyCity
            updateLocalFilters({
                propertyCountry: '',
                propertyState: [],
                propertyCity: [],
                definedArea: selectedPredefinedLocation || '',
            });
        } else {
            // When disabling predefined locations, clear definedArea
            updateLocalFilters({
                definedArea: '',
                propertyCountry: region?.name || '',
                propertyState: [],
                propertyCity: [],
            });
            setSelectedPredefinedLocation('');
        }
    };

    const handlePredefinedLocationChange = (event: any) => {
        const value = event.target.value;
        setSelectedPredefinedLocation(value);
        updateLocalFilters({
            propertyCountry: '',
            propertyState: [],
            propertyCity: [],
            definedArea: value,
        });
    };

    const applyFilters = () => {
        const filteredLocalFilter = Object.entries(localFilters)
            .filter(([_, value]) => value || value === 0)
            .reduce((acc: Record<string, any>, [key, value]) => {
                acc[key] = value;
                return acc;
            }, {});
        setPropertyRequestQueryFilter({
            ...filteredLocalFilter,
            ...(filteredLocalFilter.commission && { commission: parseInt(filteredLocalFilter.commission, 10) }),
            ...(filteredLocalFilter.urgency && { urgency: urgencyOptions.find((option) => option.label === filteredLocalFilter.urgency)?.value }),
        });
        onClose();
    };

    const hasAnyTruthyValue = Object.values(localFilters).some((value) => Boolean(value));

    return (
        <>
            <ModalLayout visible={visible} showCloseIcon onClose={onClose} zIndex={20}>
                <div className="lg:w-[1280px] md:w-[700px]  bg-white pt-20 ">
                    <h1 className="absolute top-0 left-0 p-4 font-medium text-xl font-poppins">Filters</h1>

                    {/* I don't know the exact location checkbox */}
                    <div className="px-4 mb-4">
                        <FormControlLabel
                            control={<Checkbox checked={usePredefinedLocations} onChange={handlePredefinedLocationsChange} color="primary" />}
                            label="Search in a specific area"
                        />
                    </div>

                    {usePredefinedLocations ? (
                        <div className="px-4 mb-4">
                            <FormControl fullWidth>
                                <InputLabel>Select Area</InputLabel>
                                <Select value={selectedPredefinedLocation} onChange={handlePredefinedLocationChange} label="Select Area">
                                    {MALLORCA_NOT_DEFINED_LOCATIONS.map((location) => (
                                        <MenuItem key={location} value={location}>
                                            {location}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    ) : (
                        <div className="flex gap-4 md:flex-row flex-col px-4">
                            <div className="h-14  md:flex-1">
                                <CountryFilter
                                    selectedRegion={region}
                                    onChanged={(region) => {
                                        setRegion!(region);
                                        updateLocalFilters({
                                            propertyState: [],
                                            propertyCity: [],
                                            propertyCountry: region.name,
                                        });
                                    }}
                                />
                            </div>
                            <div className="h-14 md:flex-1">
                                <StateFilter
                                    selectedStates={propertyRequestQueryFilter.propertyState || localFilters.propertyState}
                                    onChanged={(state) => updateLocalFilters({ propertyState: state, propertyCity: [] })}
                                    region={region!}
                                />
                            </div>
                            <div className="h-14 md:flex-1">
                                <CityFilter
                                    selectedCities={propertyRequestQueryFilter.propertyCity || localFilters.propertyCity}
                                    onChanged={(city) => updateLocalFilters({ propertyCity: city, propertyNeighborhood: [] })}
                                    region={region!}
                                    selectedStates={propertyRequestQueryFilter.propertyState || localFilters.propertyState}
                                    placeholder="Area"
                                />
                            </div>
                            <div className="h-14 md:flex-1">
                                <NeighborhoodFilter
                                    selectedNeighborhoods={propertyRequestQueryFilter.propertyNeighborhood || localFilters.propertyNeighborhood}
                                    onChanged={(neighborhood) => updateLocalFilters({ propertyNeighborhood: neighborhood })}
                                    region={region!}
                                    selectedStates={propertyRequestQueryFilter.propertyState || localFilters.propertyState}
                                    selectedCities={propertyRequestQueryFilter.propertyCity || localFilters.propertyCity}
                                    placeholder="Neighborhood"
                                />
                            </div>
                        </div>
                    )}

                    <Divider className="p-3" />
                    {/* Applied Filters */}
                    {hasAnyTruthyValue ? (
                        <AppliedFilters
                            options={formatFilterStrings(localFilters)}
                            onClearFilter={(filterType) => clearFilter(localFilters, filterType, setLocalFilters)}
                            action="clear"
                        />
                    ) : (
                        <div className="py-4 px-4">
                            <p className="border-dotted border-2 px-4 py-2 border-gray-300 w-fit rounded-lg">No Filter Applied</p>
                        </div>
                    )}

                    <Divider className="p-1" />
                    {/* Other  filters */}
                    {/* Budget */}
                    <div className="flex lg:flex-row flex-col p-4 mt-4 gap-4 ">
                        <RangeFilter
                            title="Budget"
                            description="Adjust to set your minimum and maximum budget"
                            unit="€"
                            Icon={Budget}
                            limit={[0, 50000000]}
                            steps={[
                                { max: 500000, step: 25000 },
                                { max: 1000000, step: 50000 },
                                { max: 5000000, step: 100000 },
                                { max: 10000000, step: 500000 },
                                { max: 50000000, step: 1000000 },
                            ]}
                            classNames="flex flex-col relative"
                            onChanged={(range) => {
                                if (range[0] > range[1]) {
                                    setErrors({ ...errors, maxBudget: 'Maximum budget cannot be less than minimum budget' });
                                } else {
                                    setErrors({ ...errors, maxBudget: '', minBudget: '' });
                                }
                                updateLocalFilters({ minBudget: range[0], maxBudget: range[1] });
                            }}
                            value={[localFilters.minBudget || 0, localFilters.maxBudget || 0]}
                            isShownClearButton
                            error={errors.maxBudget || errors.minBudget}
                        />
                    </div>
                    <div className="border border-my-gray/40 mx-4 mt-2" />
                    {/* Constructed Area and Plot Area */}
                    <div className="flex lg:flex-row flex-col p-4 mt-4 gap-4 ">
                        <RangeFilter
                            title="Living Size"
                            description="Set your preferred minimum and maximum area in square meters"
                            unit="m²"
                            Icon={ConstructedArea}
                            limit={[0, 8000]}
                            steps={[
                                { max: 500, step: 50 },
                                { max: 2000, step: 100 },
                                { max: 8000, step: 500 },
                            ]}
                            classNames="flex flex-col relative"
                            onChanged={(range) => {
                                if (range[0] > range[1]) {
                                    setErrors({
                                        ...errors,
                                        maxConstructionArea: 'Maximum Living Size cannot be less than minimum Living Size',
                                    });
                                } else {
                                    setErrors({ ...errors, maxConstructionArea: '', minConstructionArea: '' });
                                }
                                updateLocalFilters({ minConstructionArea: range[0], maxConstructionArea: range[1] });
                            }}
                            isShownClearButton
                            value={[localFilters.minConstructionArea || 0, localFilters.maxConstructionArea || 0]}
                            error={errors.maxConstructionArea || errors.minConstructionArea}
                        />{' '}
                        <RangeFilter
                            title="Plot Area"
                            description="Set your preferred minimum and maximum area in square meters"
                            unit="m²"
                            Icon={ConstructedArea}
                            limit={[0, 5000]}
                            steps={[
                                { max: 5000, step: 100 },
                                { max: 15000, step: 1000 },
                                { max: 100000, step: 5000 },
                            ]}
                            classNames="flex flex-col relative"
                            onChanged={(range) => {
                                if (range[0] > range[1]) {
                                    setErrors({ ...errors, maxPlotArea: 'Maximum construction area cannot be less than minimum construction area' });
                                } else {
                                    setErrors({ ...errors, maxPlotArea: '', minPlotArea: '' });
                                }
                                updateLocalFilters({ minPlotArea: range[0], maxPlotArea: range[1] });
                            }}
                            isShownClearButton
                            value={[localFilters.minPlotArea || 0, localFilters.maxPlotArea || 0]}
                            error={errors.maxPlotArea || errors.minPlotArea}
                        />
                    </div>
                    <div className="border border-my-gray/40 mx-4 mt-2" />
                    {/* Property type and Bedrooms Filter */}
                    <div className="flex lg:flex-row flex-col p-4 mt-4 gap-4 ">
                        <div
                            className={`lg:w-1/2 w-full p-5 border rounded-lg font-poppins relative ${Boolean(localFilters.minBedrooms) || Boolean(localFilters.maxBedrooms) ? 'border-my-blue border-2' : 'border-gray-300'}`}
                        >
                            <div className="flex gap-2 py-2 mt-2">
                                <Bed className="h-6 w-6" />
                                <p className="font-medium">Bedrooms</p>
                            </div>
                            <h3 className="my-2 text-my-black/50 font-normal text-[12px]">Set your preferred minimum and maximum number of bedrooms</h3>
                            <div className="flex gap-4 flex-1">
                                <Dropdown
                                    options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                                    isShown={false}
                                    value={localFilters.minBedrooms}
                                    onChange={(value) => {
                                        const numValue = Number(value);
                                        if (numValue > ((localFilters.maxBedrooms as number) || 0)) {
                                            setErrors({ ...errors, minBedrooms: 'Minimum bedrooms cannot be greater than maximum bedrooms' });
                                        } else {
                                            setErrors({ ...errors, minBedrooms: '', maxBedrooms: '' });
                                        }
                                        updateLocalFilters({ minBedrooms: numValue });
                                    }}
                                    showClearButton={false}
                                />
                                <h2 className="self-center text-my-black/50 font-normal text-[12px]">To</h2>
                                <Dropdown
                                    options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                                    isShown={false}
                                    value={localFilters.maxBedrooms}
                                    onChange={(value) => {
                                        const numValue = Number(value);
                                        if (numValue < ((localFilters.minBedrooms as number) || 0)) {
                                            setErrors({ ...errors, maxBedrooms: 'Maximum bedrooms cannot be less than minimum bedrooms' });
                                        } else {
                                            setErrors({ ...errors, maxBedrooms: '', minBedrooms: '' });
                                        }
                                        updateLocalFilters({ maxBedrooms: numValue });
                                    }}
                                    showClearButton={false}
                                />
                            </div>
                            {(errors.maxBedrooms || errors.minBedrooms) && (
                                <p className="text-red-500 text-sm !mt-2">{errors.maxBedrooms || errors.minBedrooms}</p>
                            )}
                            {(Boolean(localFilters.minBedrooms) || Boolean(localFilters.maxBedrooms)) && (
                                <button className="absolute top-2 right-3 text-my-blue" onClick={() => updateLocalFilters({ minBedrooms: 0, maxBedrooms: 0 })}>
                                    Clear
                                </button>
                            )}
                        </div>
                        <div
                            className={`lg:w-1/2 w-full p-5 border rounded-lg font-poppins relative ${Boolean(localFilters.minBathrooms) || Boolean(localFilters.maxBathrooms) ? 'border-my-blue border-2' : 'border-gray-300'}`}
                        >
                            <div className="flex gap-2 py-2 mt-2">
                                <Bed className="h-6 w-6" />
                                <p className="font-medium">Bathrooms</p>
                            </div>
                            <h3 className="my-2 text-my-black/50 font-normal text-[12px]">Set your preferred minimum and maximum number of bathrooms</h3>
                            <div className="flex gap-4 flex-1">
                                <Dropdown
                                    options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                                    isShown={false}
                                    value={localFilters.minBathrooms}
                                    onChange={(value) => {
                                        const numValue = Number(value);
                                        if (numValue > ((localFilters.maxBathrooms as number) || 0)) {
                                            setErrors({ ...errors, minBathrooms: 'Minimum bedrooms cannot be greater than maximum bedrooms' });
                                        } else {
                                            setErrors({ ...errors, minBathrooms: '', maxBathrooms: '' });
                                        }
                                        updateLocalFilters({ minBathrooms: numValue });
                                    }}
                                    showClearButton={false}
                                />
                                <h2 className="self-center text-my-black/50 font-normal text-[12px]">To</h2>
                                <Dropdown
                                    options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                                    isShown={false}
                                    value={localFilters.maxBathrooms}
                                    onChange={(value) => {
                                        const numValue = Number(value);
                                        if (numValue < ((localFilters.minBathrooms as number) || 0)) {
                                            setErrors({ ...errors, maxBathrooms: 'Maximum bedrooms cannot be less than minimum bedrooms' });
                                        } else {
                                            setErrors({ ...errors, maxBathrooms: '', minBathrooms: '' });
                                        }
                                        updateLocalFilters({ maxBathrooms: numValue });
                                    }}
                                    showClearButton={false}
                                />
                            </div>
                            {(errors.maxBathrooms || errors.minBathrooms) && (
                                <p className="text-red-500 text-sm !mt-2">{errors.maxBathrooms || errors.minBathrooms}</p>
                            )}
                            {(Boolean(localFilters.minBathrooms) || Boolean(localFilters.maxBathrooms)) && (
                                <button
                                    className="absolute top-2 right-3 text-my-blue"
                                    onClick={() => updateLocalFilters({ minBathrooms: 0, maxBathrooms: 0 })}
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="border border-my-gray/40 mx-4 mt-2" />
                    {/* Buyer Profile and Property Type Filters */}
                    <div className="flex lg:flex-row flex-col p-4 mt-4 gap-4">
                        <Dropdown
                            Icon={DefaultProperty}
                            title="Buyer Profile"
                            description="Filter by the type of buyer"
                            options={availableProfiles}
                            value={localFilters.buyerProfile}
                            onChange={(value) => updateLocalFilters({ buyerProfile: value as string })}
                            widthClass="w-full"
                        />
                        <Dropdown
                            Icon={FeatureBuildAreaSvg}
                            title="Property Type"
                            description="Select a property type to filter listings that match your needs"
                            options={PROPERTY_TYPES}
                            value={localFilters.propertyType}
                            onChange={(value) => updateLocalFilters({ propertyType: value as string })}
                            widthClass="w-full"
                        />
                    </div>
                    <div className="border border-my-gray/40 mx-4 mt-2" />
                    {/* Urgency and Commission */}
                    <div className="flex lg:flex-row flex-col p-4 mt-4 gap-4">
                        <Dropdown
                            Icon={Urgency}
                            title="Urgency"
                            description="Filter by the client's preffered timeframe"
                            options={['Immediate (0-1 month)', 'Short Term (1-3 months)', 'Medium Term (3-6 months)', 'Long Term (6+ months)', 'Flexible']}
                            value={localFilters.urgency}
                            onChange={(value) => updateLocalFilters({ urgency: value as string })}
                            widthClass="w-full"
                        />

                        <RangeFilter
                            title="Commission"
                            description="Narrow results by the offered commission percentage"
                            unit="%"
                            Icon={Commission}
                            limit={[0, 50]}
                            steps={[
                                { max: 5, step: 0.5 },
                                { max: 10, step: 1 },
                                { max: 50, step: 5 },
                            ]}
                            classNames="flex flex-col relative"
                            onChanged={(range) => {
                                if (range[0] > range[1]) {
                                    setErrors({ ...errors, commission: 'Maximum commission cannot be less than minimum commission' });
                                } else {
                                    setErrors({ ...errors, commission: '' });
                                }
                                updateLocalFilters({ minCommission: range[0], maxCommission: range[1] });
                            }}
                            isShownClearButton
                            value={[localFilters.minCommission || 0, localFilters.maxCommission || 0]}
                        />
                    </div>
                    {errors.commission && <p className="text-red-500 text-sm !mt-2 flex justify-end mr-6">{errors.commission}</p>}
                    <div className="border border-my-gray/40 mx-4 mt-2" />
                    {/*  */}
                    <div className="flex justify-end  p-4 mt-4 gap-4 bg-gray-100 border border-gray-200 sticky bottom-0 ">
                        <button
                            className={`bg-blue-500 text-white px-4 py-3 rounded-lg ${!hasAnyTruthyValue || Object.values(errors).some((error) => error) ? 'opacity-50' : ''}`}
                            onClick={applyFilters}
                            disabled={!hasAnyTruthyValue || Object.values(errors).some((error) => error)}
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            </ModalLayout>
        </>
    );
}
